
import {
  computed, defineComponent, reactive, ref,
} from 'vue';
import { useSelecionarBase } from '@/core/composables/SelecionarBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import ServicoProduto from '@/servicos/Cadastros/Produtos/ServicoProduto';
import { IDTOProdutoDefinicaoMovimento } from '@/models/DTO/Cadastros/Produtos/IDTOProdutoDefinicaoMovimento';

export default defineComponent({
  name: 'SelecionarProdutoFrenteCaixa',
  props: {
    codigoSelecionado: {
      type: Number || undefined,
    },
    descricao: {
      type: String,
      default: '',
    },
    codigoUnidadePrincipal: {
      type: Number,
      default: 0,
    },
    fatorUnidadePrincipal: {
      type: Number,
      default: 1,
    },
    codigoInterno: {
      type: String,
      default: '',
    },
    codigoBarras: {
      type: String,
      default: '',
    },
    origemMercadoria: {
      type: Number,
      default: 0,
    },
    codigoNcm: {
      type: Number,
      default: 0,
    },
    ncm: {
      type: String,
      default: '',
    },
    siglaUnidade: {
      type: String,
      default: '',
    },
    empresas: {
      type: Array as () => number[],
      required: true,
    },
    placeholder: {
      type: String,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    dataAttributeColuna: {
      type: String,
      default: '',
    },
    classCss: {
      type: String,
      default: '',
    },
    limparSelecao: {
      type: Boolean,
      default: true,
    },
    filtrarVinculoEstoqueEntrada: {
      type: Boolean,
      default: false,
    },
    filtrarVinculoEstoqueSaida: {
      type: Boolean,
      default: false,
    },
    buscarDadosAtualizados: {
      type: Boolean,
      default: false,
    },
    emDigitacao: {
      type: Boolean,
      default: false,
    },
    valorProduto: {
      type: Number,
      default: 0,
    },
    codigoItemTabelaPreco: {
      type: Number,
      default: 0,
    },
    quantidade: {
      type: Number,
      default: 1,
    },
    tabelaPreco: {
      type: Number,
      required: true,
    },
    tipoPreco: {
      type: Number,
      required: true,
    },
    codigoItemEstoque: {
      type: Number,
      default: 0,
    },
    codigoEstoque: {
      type: Number,
      required: true,
    },
  },
  emits: ['update:codigoSelecionado', 'update:emDigitacao', 'update:descricao', 'update:codigoUnidadePrincipal', 'update:fatorUnidadePrincipal', 'update:codigoInterno', 'update:codigoBarras', 'update:origemMercadoria', 'update:codigoNcm', 'update:ncm', 'update:siglaUnidade', 'update:buscarDadosAtualizados', 'update:valorProduto', 'change', 'blur', 'valorDigitado', 'update:codigoItemTabelaPreco', 'update:quantidade', 'update:codigoItemEstoque'],
  setup(props, { emit }) {
    const {
      selecionarBase,
      instanciaParametrosConsultaRapidaPorCodigo, instanciaParametrosConsultaRapidaPesquisa,
    } = useSelecionarBase(props, emit);

    const refSelecionarProduto = ref<HTMLElement | null>(null);
    const focoCampoProduto = ref<boolean>(false);
    let debounce = 0;
    let debounceEnter = 0;
    let tempoUltimaDigitacao = 0;
    const tempoPadraoLeitorCodigoBarras = 100; // Tempo padrão em milissegundos para considerar como entrada de leitor de código de barras
    const servicoProduto = new ServicoProduto();

    const state = reactive({
      listaDefinicoes: [] as IDTOProdutoDefinicaoMovimento[],
      codigoSelecionado: '',
    });

    const computedEmDigitacao = computed({
      get: () => props.emDigitacao,
      set: (val: boolean) => {
        emit('update:emDigitacao', val);
      },
    });

    const computedDescricao = computed({
      get: () => props.descricao,
      set: (val: string) => {
        emit('update:descricao', val);
      },
    });

    const computedCodigoUnidadePrincipal = computed({
      get: () => props.codigoUnidadePrincipal,
      set: (val: number) => {
        emit('update:codigoUnidadePrincipal', val);
      },
    });

    const computedFatorUnidadePrincipal = computed({
      get: () => props.fatorUnidadePrincipal,
      set: (val: number) => {
        emit('update:fatorUnidadePrincipal', val);
      },
    });

    const computedCodigoInterno = computed({
      get: () => props.codigoInterno,
      set: (val: string) => {
        emit('update:codigoInterno', val);
      },
    });

    const computedCodigoBarras = computed({
      get: () => props.descricao,
      set: (val: string) => {
        emit('update:codigoBarras', val);
      },
    });

    const computedOrigemMercadoria = computed({
      get: () => props.origemMercadoria,
      set: (val: number) => {
        emit('update:origemMercadoria', val);
      },
    });

    const computedCodigoNcm = computed({
      get: () => props.codigoNcm,
      set: (val: number) => {
        emit('update:codigoNcm', val);
      },
    });

    const computedNcm = computed({
      get: () => props.ncm,
      set: (val: string) => {
        emit('update:ncm', val);
      },
    });

    const computedBuscarDadosAtualizados = computed({
      get: () => props.buscarDadosAtualizados,
      set: (val: boolean) => {
        emit('update:buscarDadosAtualizados', val);
      },
    });

    const computedSiglaUnidade = computed({
      get: () => props.siglaUnidade,
      set: (val: string) => {
        emit('update:siglaUnidade', val);
      },
    });

    const computedValorProduto = computed({
      get: () => props.valorProduto,
      set: (val: number) => {
        emit('update:valorProduto', val);
      },
    });

    const computedCodigoItemTabelaPreco = computed({
      get: () => props.codigoItemTabelaPreco,
      set: (val: number) => {
        emit('update:codigoItemTabelaPreco', val);
      },
    });

    const computedQuantidade = computed({
      get: () => props.quantidade,
      set: (val: number) => {
        emit('update:quantidade', val);
      },
    });

    const computedCodigoItemEstoque = computed({
      get: () => props.codigoItemEstoque,
      set: (val: number) => {
        emit('update:codigoItemEstoque', val);
      },
    });

    function verificacaoPreBusca(valor: any):boolean {
      if (selecionarBase.emDigitacao) { return false; }
      if (!UtilitarioGeral.valorValido(valor)) { return false; }
      if (!(valor > 0)) { return false; }

      if (props.codigoSelecionado === 0) {
        return false;
      }
      // Verifica se já tem uma lista de opções e tenta procurar o valor selecionado nela pra evitar consumo no servidor
      if (UtilitarioGeral.validaLista(state.listaDefinicoes)) {
        const existeLista = state.listaDefinicoes.find((c) => c.codigo === props.codigoSelecionado);
        if (existeLista !== undefined) {
          return false;
        }
      }

      return true;
    }

    function verificacaoUltimaPesquisa(valor: any):boolean {
      if (valor === selecionarBase.valorUltimaPesquisa) {
        if (UtilitarioGeral.validaLista(state.listaDefinicoes) && state.listaDefinicoes.length > 1) { return false; }
      }

      return true;
    }

    function aguardarConclusaoCarregamento(computedCodigoSelecionado: number):boolean {
      return (!selecionarBase.emDigitacao && !UtilitarioGeral.validaLista(state.listaDefinicoes) && (UtilitarioGeral.validaCodigo(computedCodigoSelecionado)));
    }

    function comportamentoPadraoSemResultado(): void{
      if (!UtilitarioGeral.validaLista(state.listaDefinicoes)) {
        if (UtilitarioGeral.validaCodigo(props.codigoSelecionado)) {
          emit('update:codigoSelecionado', 0);
        }
      }
    }

    function change(codigoSelecionado: number) {
      computedEmDigitacao.value = false;
      emit('change', codigoSelecionado);
    }

    function adicionarDefinicaoPorPropriedades(codigo:number) {
      const definicao: IDTOProdutoDefinicaoMovimento = {} as IDTOProdutoDefinicaoMovimento;
      definicao.codigo = codigo;
      definicao.codigoInterno = props.codigoInterno;
      definicao.codigoBarras = props.codigoBarras;
      definicao.produto = props.descricao;
      definicao.codigoUnidadePrincipal = props.codigoUnidadePrincipal;
      definicao.fatorUnidadePrincipal = props.fatorUnidadePrincipal;
      definicao.origemMercadoria = props.origemMercadoria;
      definicao.codigoNcm = props.codigoNcm;
      definicao.ncm = props.ncm;
      definicao.siglaUnidade = props.siglaUnidade;
      definicao.valorProduto = props.valorProduto;
      state.listaDefinicoes.push(definicao);
    }

    async function obterProdutoPorCodigo(valor: any) {
      if (!verificacaoPreBusca(valor)) return;

      state.listaDefinicoes = [];
      if (!props.buscarDadosAtualizados && valor > 0) {
        adicionarDefinicaoPorPropriedades(valor);
      } else {
        const parametrosConsultaRapida = instanciaParametrosConsultaRapidaPorCodigo(valor.toString(), true);
        state.listaDefinicoes = await servicoProduto.consultaRapidaDefinicoesMovimentos(parametrosConsultaRapida, true);
        if (UtilitarioGeral.validaLista(state.listaDefinicoes)) {
          const definicaoProduto = state.listaDefinicoes.find((c) => c.codigo === valor);
          if (definicaoProduto !== undefined && definicaoProduto != null) {
            computedDescricao.value = definicaoProduto.produto;
            computedCodigoUnidadePrincipal.value = definicaoProduto.codigoUnidadePrincipal;
            computedFatorUnidadePrincipal.value = definicaoProduto.fatorUnidadePrincipal;
            computedCodigoInterno.value = definicaoProduto.codigoInterno;
            computedCodigoBarras.value = definicaoProduto.codigoBarras;
            computedOrigemMercadoria.value = definicaoProduto.origemMercadoria;
            computedCodigoNcm.value = definicaoProduto.codigoNcm;
            computedNcm.value = definicaoProduto.ncm;
            computedSiglaUnidade.value = definicaoProduto.siglaUnidade;
            computedBuscarDadosAtualizados.value = false;
            computedValorProduto.value = definicaoProduto.valorProduto;
            computedCodigoItemTabelaPreco.value = definicaoProduto.codigoItemTabelaPreco;
            computedCodigoItemEstoque.value = definicaoProduto.codigoItemEstoque;
          }
        }
        comportamentoPadraoSemResultado();
      }
    }

    function obterProduto():any {
      if (props.codigoSelecionado === 0) {
        return undefined;
      }
      obterProdutoPorCodigo(props.codigoSelecionado);
      return props.codigoSelecionado;
    }

    const computedCodigoSelecionado = computed({
      get: () => obterProduto(),
      set: (val: number) => {
        if (!props.buscarDadosAtualizados && !UtilitarioGeral.validaLista(state.listaDefinicoes)) {
          state.listaDefinicoes = [];
          adicionarDefinicaoPorPropriedades(val);
        } else {
          const definicaoProduto = state.listaDefinicoes.find((c) => c.codigo === val);
          if (definicaoProduto !== undefined && definicaoProduto != null) {
            computedDescricao.value = definicaoProduto.produto;
            computedCodigoUnidadePrincipal.value = definicaoProduto.codigoUnidadePrincipal;
            computedFatorUnidadePrincipal.value = definicaoProduto.fatorUnidadePrincipal;
            computedCodigoInterno.value = definicaoProduto.codigoInterno;
            computedCodigoBarras.value = definicaoProduto.codigoBarras;
            computedOrigemMercadoria.value = definicaoProduto.origemMercadoria;
            computedCodigoNcm.value = definicaoProduto.codigoNcm;
            computedNcm.value = definicaoProduto.ncm;
            computedSiglaUnidade.value = definicaoProduto.siglaUnidade;
            computedValorProduto.value = definicaoProduto.valorProduto;
            computedCodigoItemTabelaPreco.value = definicaoProduto.codigoItemTabelaPreco;
            computedCodigoItemEstoque.value = definicaoProduto.codigoItemEstoque;
          }
        }
        emit('update:codigoSelecionado', val);
      },
    });

    function blur() {
      selecionarBase.emDigitacao = false;
      computedEmDigitacao.value = false;
      focoCampoProduto.value = false;
      emit('blur');
    }

    function dispararEnter() {
      state.codigoSelecionado = '';
      clearTimeout(debounceEnter);
      debounceEnter = setTimeout(async () => {
        if (refSelecionarProduto.value) {
          const enterEvent = new KeyboardEvent('keydown', {
            key: 'Enter',
            code: 'Enter',
            bubbles: true,
            cancelable: true,
          });
          refSelecionarProduto.value.dispatchEvent(enterEvent);
        }
      }, 300);
    }

    function aoSelecionar(codigo: string) {
      // Localiza o objeto completo na lista original
      const produtoSelecionado = state.listaDefinicoes.find((item) => item.codigoInterno === codigo);

      if (produtoSelecionado) {
        // Aqui você pode armazenar em outra variável ou emitir evento
        computedCodigoSelecionado.value = produtoSelecionado.codigo;
        computedDescricao.value = produtoSelecionado.produto;
        computedCodigoUnidadePrincipal.value = produtoSelecionado.codigoUnidadePrincipal;
        computedFatorUnidadePrincipal.value = produtoSelecionado.fatorUnidadePrincipal;
        computedCodigoInterno.value = produtoSelecionado.codigoInterno;
        computedCodigoBarras.value = produtoSelecionado.codigoBarras;
        computedOrigemMercadoria.value = produtoSelecionado.origemMercadoria;
        computedCodigoNcm.value = produtoSelecionado.codigoNcm;
        computedNcm.value = produtoSelecionado.ncm;
        computedSiglaUnidade.value = produtoSelecionado.siglaUnidade;
        computedBuscarDadosAtualizados.value = false;
        computedValorProduto.value = produtoSelecionado.valorProduto;
        computedCodigoItemTabelaPreco.value = produtoSelecionado.codigoItemTabelaPreco;
        computedQuantidade.value = produtoSelecionado.quantidade;
        computedCodigoItemEstoque.value = produtoSelecionado.codigoItemEstoque;
        selecionarBase.emDigitacao = false;
        dispararEnter();
        emit('valorDigitado');
      }
    }

    async function pesquisarProduto(valorPesquisa: any, quantidade: number) {
      selecionarBase.emDigitacao = true;

      if (!verificacaoUltimaPesquisa(valorPesquisa)) return;

      const horaAtual = new Date().getTime();

      tempoUltimaDigitacao = horaAtual;
      selecionarBase.buscandoDados = true;
      state.listaDefinicoes = [];
      clearTimeout(debounce);
      debounce = setTimeout(async () => {
        const parametrosConsultaRapida = instanciaParametrosConsultaRapidaPesquisa(valorPesquisa, true);
        state.listaDefinicoes = await servicoProduto.consultaRapidaFrenteCaixa(parametrosConsultaRapida, props.tipoPreco, props.tabelaPreco, props.codigoEstoque);
        selecionarBase.valorUltimaPesquisa = valorPesquisa;
        computedEmDigitacao.value = true;
        selecionarBase.buscandoDados = false;

        if (UtilitarioGeral.validaLista(state.listaDefinicoes)) {
          if (state.listaDefinicoes.length === 1) {
            const codigoProduto = state.listaDefinicoes[0];
            state.listaDefinicoes[0].quantidade = quantidade;
            computedCodigoSelecionado.value = codigoProduto.codigo;
            computedDescricao.value = codigoProduto.produto;
            computedCodigoUnidadePrincipal.value = codigoProduto.codigoUnidadePrincipal;
            computedFatorUnidadePrincipal.value = codigoProduto.fatorUnidadePrincipal;
            computedCodigoInterno.value = codigoProduto.codigoInterno;
            computedCodigoBarras.value = codigoProduto.codigoBarras;
            computedOrigemMercadoria.value = codigoProduto.origemMercadoria;
            computedCodigoNcm.value = codigoProduto.codigoNcm;
            computedNcm.value = codigoProduto.ncm;
            computedSiglaUnidade.value = codigoProduto.siglaUnidade;
            computedBuscarDadosAtualizados.value = false;
            computedValorProduto.value = codigoProduto.valorProduto;
            computedCodigoItemTabelaPreco.value = codigoProduto.codigoItemTabelaPreco;
            computedQuantidade.value = quantidade;
            computedCodigoItemEstoque.value = codigoProduto.codigoItemEstoque;
            selecionarBase.emDigitacao = false;
            // dispararEnter();
            // emit('valorDigitado');
          } else {
            for (let i = 0; i < state.listaDefinicoes.length; i += 1) {
              state.listaDefinicoes[i].quantidade = quantidade;
            }
          }
        }
      }, 600);
    }
    async function aoPesquisarOuDigitar(valorPesquisa: string) {
      selecionarBase.emDigitacao = true;

      let quantidadeInformada = 1;
      let termoPesquisa = valorPesquisa;
      const matchQuantidade = valorPesquisa.match(/^(\d+)\*(.+)$/);
      if (matchQuantidade) {
        quantidadeInformada = parseInt(matchQuantidade[1], 10);
        termoPesquisa = matchQuantidade[2]; // remove a parte da quantidade para pesquisa
      }

      if (!verificacaoUltimaPesquisa(termoPesquisa)) return;

      const horaAtual = new Date().getTime();
      const tempoDiferencaDigitacao = horaAtual - tempoUltimaDigitacao;
      let utilizouLeitorCodigoBarras = false;

      if (tempoDiferencaDigitacao !== 0 && tempoDiferencaDigitacao < tempoPadraoLeitorCodigoBarras) {
        utilizouLeitorCodigoBarras = true;
      }

      tempoUltimaDigitacao = horaAtual;
      selecionarBase.buscandoDados = true;
      state.listaDefinicoes = [];
      clearTimeout(debounce);

      const executarBusca = async () => {
        // Define o que considera como código de barras
        const ehCodigoBarras = /^[0-9]{6,}$/.test(termoPesquisa);

        if (ehCodigoBarras) {
          const codigoProduto = await servicoProduto.obterProdutoPorCodigoBarras(termoPesquisa, props.empresas[0], props.tabelaPreco, props.tipoPreco, props.codigoEstoque);
          selecionarBase.valorUltimaPesquisa = termoPesquisa;
          computedEmDigitacao.value = true;
          selecionarBase.buscandoDados = false;

          if (UtilitarioGeral.objetoValido(codigoProduto)) {
            computedCodigoSelecionado.value = codigoProduto.codigo;
            computedDescricao.value = codigoProduto.produto;
            computedCodigoUnidadePrincipal.value = codigoProduto.codigoUnidadePrincipal;
            computedFatorUnidadePrincipal.value = codigoProduto.fatorUnidadePrincipal;
            computedCodigoInterno.value = codigoProduto.codigoInterno;
            computedCodigoBarras.value = codigoProduto.codigoBarras;
            computedOrigemMercadoria.value = codigoProduto.origemMercadoria;
            computedCodigoNcm.value = codigoProduto.codigoNcm;
            computedNcm.value = codigoProduto.ncm;
            computedSiglaUnidade.value = codigoProduto.siglaUnidade;
            computedBuscarDadosAtualizados.value = false;
            computedValorProduto.value = codigoProduto.valorProduto;
            computedCodigoItemTabelaPreco.value = codigoProduto.codigoItemTabelaPreco;
            computedQuantidade.value = quantidadeInformada;
            computedCodigoItemEstoque.value = codigoProduto.codigoItemEstoque;
            selecionarBase.emDigitacao = false;
            dispararEnter();
            emit('valorDigitado');
            return; // achou pelo código de barras, sai
          }
        }
        await pesquisarProduto(termoPesquisa, quantidadeInformada);
      };

      if (utilizouLeitorCodigoBarras) {
        executarBusca(); // leitor de código de barras: executa sem debounce
      } else {
        debounce = setTimeout(executarBusca, 300); // digitação manual: debounce
      }
    }

    function focus() {
      if (!UtilitarioGeral.validaCodigo(state.codigoSelecionado)) {
        computedEmDigitacao.value = true;
        pesquisarProduto('', 1);
      }

      if (refSelecionarProduto.value?.focus) {
        refSelecionarProduto.value.focus();
      }
    }

    return {
      props,
      state,
      selecionarBase,
      refSelecionarProduto,
      aguardarConclusaoCarregamento,
      pesquisarProduto,
      computedCodigoSelecionado,
      computedEmDigitacao,
      focoCampoProduto,
      change,
      focus,
      blur,
      computedBuscarDadosAtualizados,
      computedValorProduto,
      aoPesquisarOuDigitar,
      aoSelecionar,
    };
  },
});
